import { Input, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Keyboard } from "react-native";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { schema } from "../utils";
import { VDBIcons } from "@utils/VDBIcons";
import TotalPriceContainer from "./TotalPriceContainer";
import { useEffect, useState } from "react";
import { useDynamicFormState } from "@scenes/bulk-request/context/DynamicFormContext";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import React from "react";
import { ProductCategory } from "@utils/constants";
import { CartItemListResponse } from "@src/types/CartItemListResponse";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useOverlayConfig } from "@redux/slices/globalSlice";
import { AxiosResponse } from "axios";
import { useAutoEnterKeySubmit } from "../../../hooks/useAutoEnterKeySubmit";

const CheckoutForm = ({
  totalPrice,
  itemList,
  showPromo,
  productType,
  setCouponCode,
  refetchCartData,
  cartData,
  cartAPIFetching,
}: {
  totalPrice?: string;
  itemList?: any;
  showPromo?: boolean;
  productType: ProductCategory;
  setCouponCode?: any;
  refetchCartData?: any;
  cartData?: AxiosResponse<CartItemListResponse>;
  couponCode?: string;
  cartAPIFetching: boolean;
}) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const getOverlayConfig = useOverlayConfig();
  const [apiData, setApiData] = useState({});
  const { dynamicFormValues, setDynamicFormValues, clearRecipientVersion } =
    useDynamicFormState();
  const [disableEnterKey, setDisableEnterKey] = useState(false);

  const { control, getValues, setValue, setError } = useForm({
    resolver: yupResolver(schema()),
  });

  useEffect(() => {
    if (!clearRecipientVersion) return;
    setValue("comments", "");
    setDynamicFormValues("comments", "");
  }, [clearRecipientVersion]);

  useEffect(() => {
    if (!cartAPIFetching) {
      const data = cartData?.data?.response;
      if (data?.coupon) {
        setCouponCode(data.coupon.coupon_code);
        setApiData(data.coupon ?? null);
        setValue("promo", data.coupon.coupon_code);
        if (data.coupon !== apiData) {
          showToast(
            t(data.coupon_message ?? ""),
            undefined,
            ToastTypes.SUCCESS,
            true,
            true,
            undefined,
            undefined,
            true,
            Number(getOverlayConfig?.toastVisibilityTime) || 5000
          );
        }
      } else {
        setDisableEnterKey(false);
        if (cartData?.config?.params?.coupon_code === "remove_coupon") {
          showToast(
            t(
              "orderCart.promoCode.removeCode.text",
              "Promo code removed successfully."
            ),
            undefined,
            ToastTypes.ERROR,
            true,
            true,
            undefined,
            undefined,
            true,
            Number(getOverlayConfig?.toastVisibilityTime) || 5000
          );
        } else {
          if (data?.coupon_message) {
            setError("promo", {
              type: "value",
              message: t(data?.coupon_message),
            });
          }
        }
      }
    }
  }, [cartData, cartAPIFetching]);

  useEffect(() => {
    if (totalPrice) {
      setDynamicFormValues("totalPrice", totalPrice);
    }
  }, [totalPrice]);

  const handleCheckCoupon = async () => {
    const code = getValues("promo")?.trim();
    if (code && !cartData?.data?.response.coupon) {
      refetchCartData();
      setDisableEnterKey(true);
    } else {
      setDisableEnterKey(false);
      await setCouponCode("remove_coupon");
      setValue("promo", "");
      refetchCartData();
    }
  };

  useAutoEnterKeySubmit(
    handleCheckCoupon,
    disableEnterKey || getValues("promo")?.length === 0
  );

  return (
    <ParentContainer>
      <TextContainer>
        <VDBText color={theme["core-05"]} type={typography["Minor-R"]}>
          {t("orderCart.checkout.screenText.text")}
        </VDBText>
      </TextContainer>
      <Spacer size={SpaceType.Double} />
      <Controller
        control={control}
        render={({
          field: { onChange, onBlur, value },
          formState: { errors },
        }) => (
          <Input
            inputLabel={t("orderCart.input.comments.placeHolder.text")}
            placeholder={t("orderCart.input.comments.placeHolder.text")}
            leftIcon={undefined}
            hideIconContainer={true}
            onBlur={() => {
              setDynamicFormValues("comments", value);
              onBlur();
            }}
            onChangeText={onChange}
            value={value}
            error={(errors?.comments?.message ?? "") as string}
            multiline={true}
            {...(!theme.isLargeScreen && {
              blurOnSubmit: true,
              onSubmitEditing: () => {
                Keyboard.dismiss();
              },
            })}
            keyboardType="default"
            returnKeyType="done"
          />
        )}
        name="comments"
      />
      <Spacer size={SpaceType.Big} />
      {showPromo && (
        <>
          <Controller
            control={control}
            name="promo"
            render={({
              field: { onChange, onBlur, value },
              formState: { errors },
            }) => {
              return (
                <Input
                  inputLabel={t("orderCart.input.promo.placeHolder.text")}
                  placeholder={t("orderCart.input.promo.placeHolder.text")}
                  leftIcon={undefined}
                  hideIconContainer={true}
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    setError("promo", { message: "" });
                    onChange(text.trim().toUpperCase());
                    setCouponCode(text.trim().toUpperCase());
                  }}
                  value={value}
                  error={errors?.promo?.message as string}
                  keyboardType="default"
                  returnKeyType="done"
                  {...(!errors.promo?.message && {
                    rightButtonIcon:
                      Object?.keys?.(cartData?.data?.response.coupon || {})
                        ?.length > 0
                        ? VDBIcons.CLOSE_CIRCLE
                        : VDBIcons.SEND,
                  })}
                  rightButtonIconColor={!value ? "core-04" : "core-05"}
                  rightButtonDisabled={!value}
                  disabled={
                    Object?.keys?.(cartData?.data?.response.coupon || {})
                      ?.length > 0
                  }
                  rightButtonIconOnPress={handleCheckCoupon}
                />
              );
            }}
          />
        </>
      )}
      <TotalPriceContainer
        totalPrice={dynamicFormValues.totalPrice}
        theme={theme}
        typography={typography}
        t={t}
        itemList={itemList}
        userInfo={userInfo}
        productType={productType}
        cartData={cartData?.data?.response}
      />
    </ParentContainer>
  );
};

export default CheckoutForm;

const ParentContainer = styled.View``;

const TextContainer = styled.View`
  padding-horizontal: ${({ theme }) =>
    theme.isMobile ? SpaceType.Basic : 0}px;
`;
